import React, { createContext, useContext, useState, ReactNode, useEffect, useRef, useCallback } from 'react';
import { useFolderStore } from '@/store/folderStore';

interface PaginationState {
  currentPage: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
  totalEmails: number;
  emailsPerPage: number;
}

type PageChangeHandler = (page: number) => void;

interface PaginationContextType {
  paginationState: PaginationState;
  setPaginationState: React.Dispatch<React.SetStateAction<PaginationState>>;
  handlePageChange: (page: number) => void;
  registerPageChangeHandler: (handler: PageChangeHandler | null) => void;
  resetPagination: () => void;
  }

interface PaginationProviderProps {
  children: ReactNode;
}

const defaultPaginationState: PaginationState = {
    currentPage: 1,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
    totalEmails: 0,
    emailsPerPage: 20
};

const PaginationContext = createContext<PaginationContextType | undefined>(undefined);

export const PaginationProvider: React.FC<PaginationProviderProps> = ({ children }) => {
  const [paginationState, setPaginationState] = useState<PaginationState>(defaultPaginationState);
  const { currentFolder } = useFolderStore();
  const pageChangeHandlerRef = useRef<PageChangeHandler | null>(null);

  // Reset pagination when folder changes
  useEffect(() => {
    setPaginationState(defaultPaginationState);
  }, [currentFolder]);

  const registerPageChangeHandler = useCallback((handler: PageChangeHandler | null) => {
    pageChangeHandlerRef.current = handler;
  }, []);

  const handlePageChange = useCallback((page: number) => {
    if (page < 1) return;

    // Delegate to InboxList so page/URL stay in sync with a single fetch
    if (pageChangeHandlerRef.current) {
      pageChangeHandlerRef.current(page);
      return;
    }

    if (page <= paginationState.totalPages) {
      setPaginationState(prev => ({ ...prev, currentPage: page }));
    }
  }, [paginationState.totalPages]);

  const resetPagination = () => {
    setPaginationState(defaultPaginationState);
  };

  return (
    <PaginationContext.Provider value={{
      paginationState,
      setPaginationState,
      handlePageChange,
      registerPageChangeHandler,
      resetPagination
    }}>
      {children}
    </PaginationContext.Provider>
  );
};

export const usePagination = () => {
  const context = useContext(PaginationContext);
  if (!context) {
    throw new Error('usePagination must be used within a PaginationProvider');
  }
  return context;
}; 